// Shared product-card image: shows a skeleton while the real image loads,
// and silently retries a couple of times before falling back to the
// placeholder graphic — smooths over the flaky-connection case where a car
// photo intermittently fails to load, instead of leaving a broken/empty tile.
import React, { useEffect, useState } from 'react';

interface ProductImageProps {
  src?: string;
  alt: string;
  loading?: 'lazy' | 'eager';
}

const PLACEHOLDER = 'asset/images/product-placeholder.png';
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1200;

const ProductImage: React.FC<ProductImageProps> = ({ src, alt, loading = 'lazy' }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    setAttempt(0);
  }, [src]);

  const handleError = () => {
    if (attempt < MAX_RETRIES) {
      setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAY_MS);
    } else {
      setFailed(true);
      setLoaded(true);
    }
  };

  const imageSrc = !src || failed ? PLACEHOLDER : `${src}${attempt > 0 ? `?retry=${attempt}` : ''}`;

  return (
    <>
      {!loaded && (
        <div
          className="product-image-skeleton"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        />
      )}
      <img
        key={`${src || PLACEHOLDER}-${attempt}`}
        src={imageSrc}
        alt={alt}
        loading={loading}
        // `display: none` (rather than `opacity: 0`) here would remove the img's layout
        // box, and a loading="lazy" image with no box never becomes eligible for the
        // browser's native lazy-load intersection check — it would stay hidden forever.
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={handleError}
      />
    </>
  );
};

export default ProductImage;
